import { getInvoiceLifecycleStatus } from './invoiceRemoveFromPoHelpers'
import {
  hasReconciledPoAllocation,
  listReconciledPoAllocations,
} from './invoiceMultiDocRules'

export const UNRECONCILE_BUTTON_LABEL = 'Unreconcile'

export const UNRECONCILE_CONFIRM_DEFAULT =
  'Remove reconciliation from this invoice? The invoice stays linked to this purchase order. You can reconcile again when ready. Goods Receipting and Matching will refresh.'

export const UNRECONCILE_CONFIRM_MULTI_PO =
  'Remove reconciliation from the selected purchase order slice? Other reconciled slices on this invoice stay closed. The invoice file stays in OMS.'

export const UNRECONCILE_XERO_WARNING =
  'This invoice has already been exported to Xero. Unreconciling it in OMS will not automatically change the transaction in Xero.'

function normalisePo(value) {
  return String(value || '').trim()
}

export function invoiceHasReconciliationToUndo(invoice = {}) {
  const lifecycle = getInvoiceLifecycleStatus(invoice)
  if (lifecycle === 'reconciled' || lifecycle === 'sentToXero') {
    return true
  }
  return hasReconciledPoAllocation(invoice)
}

function resolveDefaultPurchaseOrderNumber(invoice = {}) {
  return (
    normalisePo(invoice.purchaseOrderNumber) ||
    normalisePo(invoice.matchSummary?.purchaseOrderNumber) ||
    (Array.isArray(invoice.matchedPurchaseOrderNumbers)
      ? normalisePo(invoice.matchedPurchaseOrderNumbers[0])
      : '')
  )
}

function listTargetPurchaseOrderNumbers(invoice = {}) {
  const fromAllocations = listReconciledPoAllocations(invoice)
    .map(row => normalisePo(row.purchaseOrderNumber))
    .filter(Boolean)

  if (fromAllocations.length) {
    return [...new Set(fromAllocations)]
  }

  const primary = resolveDefaultPurchaseOrderNumber(invoice)
  return primary ? [primary] : []
}

/**
 * Shared view-model for Invoices row unreconcile action.
 *
 * @returns {{
 *   canShow: boolean,
 *   buttonLabel: string,
 *   confirmCopy: string,
 *   needsPoSelection: boolean,
 *   reconciledPurchaseOrderNumbers: string[],
 *   defaultPurchaseOrderNumber: string,
 *   exportedToXero: boolean,
 *   xeroWarning: string,
 * }}
 */
export function getUnreconcileActionMeta(invoice = {}) {
  const empty = {
    canShow: false,
    buttonLabel: UNRECONCILE_BUTTON_LABEL,
    confirmCopy: '',
    needsPoSelection: false,
    reconciledPurchaseOrderNumbers: [],
    defaultPurchaseOrderNumber: '',
    exportedToXero: false,
    xeroWarning: '',
  }

  if (invoice?.isArchived || !invoiceHasReconciliationToUndo(invoice)) {
    return empty
  }

  const lifecycle = getInvoiceLifecycleStatus(invoice)
  const reconciledPurchaseOrderNumbers = listTargetPurchaseOrderNumbers(invoice)
  const needsPoSelection = reconciledPurchaseOrderNumbers.length > 1
  const exportedToXero = lifecycle === 'sentToXero'
  const isMultiPoPartial =
    hasReconciledPoAllocation(invoice) &&
    reconciledPurchaseOrderNumbers.length >= 1 &&
    listReconciledPoAllocations(invoice).length <
      (invoice.poAllocations || []).length

  return {
    canShow: true,
    buttonLabel: UNRECONCILE_BUTTON_LABEL,
    confirmCopy:
      needsPoSelection || isMultiPoPartial
        ? UNRECONCILE_CONFIRM_MULTI_PO
        : UNRECONCILE_CONFIRM_DEFAULT,
    needsPoSelection,
    reconciledPurchaseOrderNumbers,
    defaultPurchaseOrderNumber: reconciledPurchaseOrderNumbers[0] || '',
    exportedToXero,
    xeroWarning: exportedToXero ? UNRECONCILE_XERO_WARNING : '',
  }
}

export function canShowUnreconcileAction(invoice = {}) {
  return getUnreconcileActionMeta(invoice).canShow === true
}
